// @skade/contracts — militær hierarki-ryggrad (TO PARALLELLE AKSER).
//
// Hele SKADE (RBAC-scope, adressering, ORBAT, ESO-rollup, symbol-echelon) hviler
// på denne ene modellen, slik at «militært hierarki» ikke er spredt og
// inkonsistent, men utledes ett sted:
//
//   AKSE 1 — KOMMANDO-ECHELON (styrkens størrelse/kommandonivå):
//     Enkeltmann → Makkerpar → Lag → Tropp → Kompani → Bataljon → Brigade
//
//   AKSE 2 — HV-TERRITORIELL TILHØRIGHET (hvor i landet/strukturen):
//     Nasjonal (FOH) → HV-distrikt → HV-område
//
// En enhet har ETT echelon-nivå OG en territoriell plassering — de to aksene
// er uavhengige (en «Tropp» kan ligge i HV-01/Oslofjord like gjerne som i
// HV-12). Echelon speiler GNÅ-appens ESO-nivåer (enkeltmann/makkerpar/lag/tropp)
// og utvider dem oppover (kompani/bataljon/brigade) — samme id-er, ingen
// divergens. Ren, avhengighetsfri, testbar logikk.
import { z } from 'zod';
// ── Akse 1: Kommando-echelon ────────────────────────────────────────────────
/** Kommando-echelon, laveste → høyeste. Id-ene matcher GNÅ ESO (`level`). */
export const Echelon = z.enum([
    'enkeltmann',
    'makkerpar',
    'lag',
    'tropp',
    'kompani',
    'bataljon',
    'brigade',
]);
/** Kanonisk rekkefølge, laveste → høyeste (indeks = rang). */
export const ECHELON_ORDER = [
    'enkeltmann', 'makkerpar', 'lag', 'tropp', 'kompani', 'bataljon', 'brigade',
];
export const ECHELON_LABEL = {
    enkeltmann: 'Enkeltmann',
    makkerpar: 'Makkerpar',
    lag: 'Lag',
    tropp: 'Tropp',
    kompani: 'Kompani',
    bataljon: 'Bataljon',
    brigade: 'Brigade',
};
/** Korte forkortelser (til tett UI / symboletiketter). */
export const ECHELON_ABBR = {
    enkeltmann: 'EM',
    makkerpar: 'PAR',
    lag: 'LAG',
    tropp: 'TR',
    kompani: 'KP',
    bataljon: 'BN',
    brigade: 'BDE',
};
/** APP-6/2525 echelon-kode (SIDC pos 12). '-' = ingen markør (enkeltmann). */
export const ECHELON_SIDC = {
    enkeltmann: '-',
    makkerpar: 'A', // team/crew
    lag: 'B', // squad
    tropp: 'D', // platoon
    kompani: 'E', // company
    bataljon: 'F', // battalion
    brigade: 'H', // brigade
};
/** Rang (0 = enkeltmann … 6 = brigade). -1 for ukjent. */
export function echelonRank(e) {
    return ECHELON_ORDER.indexOf(e);
}
/** Nærmeste echelon over `e` (null for brigade). */
export function parentEchelon(e) {
    const i = echelonRank(e);
    return i >= 0 && i < ECHELON_ORDER.length - 1 ? ECHELON_ORDER[i + 1] : null;
}
/** Nærmeste echelon under `e` (null for enkeltmann). */
export function childEchelon(e) {
    const i = echelonRank(e);
    return i > 0 ? ECHELON_ORDER[i - 1] : null;
}
/** Er `a` på eller over `b` i kommandokjeden? */
export function echelonAtOrAbove(a, b) {
    return echelonRank(a) >= echelonRank(b);
}
/** Coerce vilkårlig streng til et gyldig echelon (fallback = enkeltmann). */
export function toEchelon(v) {
    const r = Echelon.safeParse(v);
    return r.success ? r.data : 'enkeltmann';
}
// ── Akse 2: HV-territoriell tilhørighet ─────────────────────────────────────
/** Territorielt nivå, høyeste → laveste geografisk oppdeling. */
export const TerritorialLevel = z.enum(['nasjonal', 'distrikt', 'område']);
export const TERRITORIAL_ORDER = ['nasjonal', 'distrikt', 'område'];
export const TERRITORIAL_LABEL = {
    nasjonal: 'Nasjonal (FOH)',
    distrikt: 'HV-distrikt',
    område: 'HV-område',
};
/** Finest angitte territorielle nivå i en adresse. */
export function territorialLevelOf(t) {
    if (t.area)
        return 'område';
    if (t.district)
        return 'distrikt';
    return 'nasjonal';
}
/** Er territoriet `child` innenfor (eller lik) `parent`? Tomt parent-felt =
 * «alle» på det nivået (nasjonal favner alt). */
export function territoryWithin(child, parent) {
    if (parent.district && parent.district !== child.district)
        return false;
    if (parent.area && parent.area !== child.area)
        return false;
    return true;
}
// ── Kanonisk enhetsadresse (begge akser) ────────────────────────────────────
/** En enhets fulle plassering: echelon (akse 1) + territorium + kommandosti. */
export const UnitAddressSchema = z.object({
    echelon: Echelon.default('lag'),
    district: z.string().max(64).optional(),
    area: z.string().max(120).optional(),
    troop: z.string().max(120).optional(), // kommandosti under område
    team: z.string().max(120).optional(),
});
/** Kort, lesbar etikett: «KP · HV-01/Oslofjord». */
export function addressLabel(a) {
    const terr = [a.district, a.area].filter(Boolean).join('/');
    return terr ? `${ECHELON_ABBR[a.echelon]} · ${terr}` : ECHELON_ABBR[a.echelon];
}
/** Fold et tre nedenfra-og-opp: kombinér nodens egen verdi med barnas
 * (allerede foldede) verdier. Ren, brukes til stridsevne- og ESO-rollup. */
export function foldTree(node, leaf, combine) {
    const childResults = node.children.map((c) => foldTree(c, leaf, combine));
    return combine(leaf(node.value), childResults);
}
/** «Verste vinner»-reduksjon: laveste rang blant elementene (eller `fallback`
 * om tom). Rang-funksjonen: lavere tall = verre. */
export function worstBy(items, rank, fallback) {
    let worst;
    for (const it of items) {
        if (worst === undefined || rank(it) < rank(worst))
            worst = it;
    }
    return worst ?? fallback;
}
//# sourceMappingURL=hierarchy.js.map